// S2 · Шаг 1 — RUSSIAN for the vertical picker (`onboarding.vertical.*`).
//
// Register notes:
//  · «Отраслевой пакет» for industry pack — the phrase a Kazakh plant director
//    would recognise; «вертикаль» is consultant-speak and means nothing on a
//    shop floor.
//  · The three-way honesty split of §3.2 has to survive translation, so the
//    headings stay sharply distinct: «Создано в вашем заводе» (real records),
//    «Хранится с пакетом» (templates), «Ожидают другие модули» (NOT created).
//  · Process nouns follow TERMINOLOGY_BASE.ru so the pack preview and the rest
//    of the product agree: наладка, брак, доработка, вторичный материал.

import type { Translated } from "../../../../i18n-shape.js";
import type { verticalEn } from "./vertical.i18n.js";

export const verticalRu: Translated<typeof verticalEn> = {
  packsHeading: "Отраслевые пакеты",
  packsSubtitle:
    "Операции, единицы, проверки и термины вашей отрасли. Посмотрите перед установкой.",
  loading: "Загружаются отраслевые пакеты…",
  errorTitle: "Не удалось загрузить отраслевые пакеты",
  errorDetail: "Каталог пакетов прочитать не удалось. Попробуйте ещё раз.",
  emptyTitle: "Доступных отраслевых пакетов нет",
  emptyBody:
    "Для вас пока нет пакета. Свяжитесь с Assessly. Настройку можно продолжить, но для шага 1 нужен пакет.",
  readOnlyNote:
    "Устанавливать отраслевой пакет может только администратор группы. Посмотреть каждый пакет вы можете и так.",
  frozenNote: "Ваше предприятие уже запущено. Устанавливать пакеты здесь больше нельзя.",
  completeHint: "Сначала установите пакет — без него шаг 1 не завершить.",

  card: {
    version: "Версия {{version}}",
    objects: "Объектов в пакете: {{count}}",
    countLine: "{{count}} — {{kind}}",
    unnamed: "Пакет без названия",
    noDescription: "В этом пакете нет описания.",
    previewCta: "Посмотреть пакет",
    installCta: "Установить этот пакет",
    switchCta: "Перейти на этот пакет",
    installedBadge: "Установлен",
    installingBadge: "Устанавливается",
    failedBadge: "Установка не удалась",
  },

  current: {
    loading: "Проверяем установку пакета…",
    errorTitle: "Не удалось прочитать текущую установку пакета",
    errorDetail:
      "Состояние установки прочитать не удалось, поэтому кнопки ниже могут быть неактуальны.",
    installedTitle: "{{name}} установлен",
    installedBody: "Версия {{version}}, завершено {{date}}.",
    installingTitle: "Устанавливается {{name}}…",
    installingBody:
      "Пакет разворачивается на вашем заводе. Страница обновляется сама; можно уйти и вернуться.",
    progress: "Создано объектов: {{done}} из {{total}}",
    failedTitle: "Установка {{name}} не удалась",
    failedBody: "Откат не выполнялся — всё уже созданное остаётся, а повтор доделает остальное.",
    failedCode: "Код ошибки: {{code}}",
    supersededTitle: "{{name}} заменён",
    supersededBody:
      "Сейчас у вашего предприятия нет действующего пакета. Установите пакет, чтобы продолжить.",
    switchCta: "Сменить пакет",
    retryCta: "Повторить установку",
    finishedUnknown: "только что",
    technicalDetail: "Технические подробности",
  },

  breakdown: {
    heading: "Что этот пакет добавил на ваш завод",
    seededHeading: "Создано на вашем заводе",
    seededNote: "Это настоящие записи, которые можно открыть и править на их собственных экранах.",
    snapshotHeading: "Хранится вместе с пакетом",
    snapshotNote:
      "Это шаблоны, которые хранятся вместе с установленным пакетом. Они используются, когда вы составляете маршруты и раскладываете предприятие; отдельными записями они не являются.",
    skippedHeading: "Ожидают другие модули",
    skippedNote:
      "Не созданы: их модуль здесь не используется. Они добавятся из пакета, когда он появится.",
    colKind: "Вид",
    colInPack: "В пакете",
    colCreated: "Создано",
    colOwner: "Принадлежит",
    pendingModule: "Ожидает модуль {{module}}",
    inSnapshot: "Хранится с пакетом",
    created: "{{done}} из {{total}}",
    totals:
      "Создано записей: {{materialized}}; хранится с пакетом: {{snapshot}}; ожидают другие модули: {{pending}}.",
  },

  kinds: {
    unit: "Единицы измерения",
    attribute_schema: "Схемы атрибутов",
    catalogue_template: "Шаблоны каталога",
    qc_checklist_template: "Чек-листы качества",
    failure_cause: "Причины несоответствий",
    waste_category: "Категории отходов",
    waste_cause: "Причины отходов",
    remnant_price_class: "Ценовые классы остатков",
    waste_settings: "Настройки учёта отходов",
    downtime_reason: "Причины простоя",
    maintenance_reason_code: "Коды причин обслуживания",
    offcut_discount_class: "Классы скидок на обрезки",
    pipeline_stage: "Этапы воронки",
    role: "Роли",
    terminology_bundle: "Словарь терминов",
    routing_step_template: "Шаблоны операций маршрута",
    work_center_template: "Шаблоны рабочих центров",
    machine_type: "Типы станков",
    label_template: "Шаблоны этикеток",
  },

  confirm: {
    installTitle: "Установить {{name}}?",
    installBody:
      "Установка наполнит ваш завод всем содержимым пакета. Она идёт в фоне, работать можно дальше.",
    switchTitle: "Перейти на {{name}}?",
    switchBody:
      "У вас уже установлен {{current}}. Переход изменит значения по умолчанию на вашем заводе.",
    consequencesHeading: "Прежде чем переходить, учтите:",
    consequenceSupersede: "Текущий пакет помечается как заменённый, а новый разворачивается.",
    consequenceAdopt:
      "Всё общее у двух пакетов — единица измерения, роль — используется повторно, а не дублируется.",
    consequenceOrphans:
      "Записи, которые появились только из старого пакета, остаются на месте и помечаются вам на проверку; автоматически ничего не удаляется.",
    consequenceAfterLive: "После запуска предприятия сменить пакет нельзя.",
    confirmInstall: "Установить пакет",
    confirmSwitch: "Сменить пакет",
    cancel: "Отмена",
    working: "Запускается…",
  },

  problem: {
    alreadyInstalledTitle: "Пакет уже установлен",
    alreadyInstalledBody:
      "У вашего предприятия уже есть действующий пакет. Нажмите «Сменить пакет», чтобы заменить его: старый пакет будет выведен из работы, а не работать вместе с новым.",
    unknownTitle: "Такого пакета больше нет",
    unknownBody:
      "Пока страница была открыта, каталог пакетов изменился. Обновите список и выберите заново.",
    unknownReload: "Обновить список пакетов",
    failedTitle: "Установку не удалось запустить",
    failedDetail: "Ничего не изменилось. Попробуйте ещё раз.",
  },

  drawer: {
    ariaLabel: "Просмотр пакета",
    close: "Закрыть",
    loading: "Загружается пакет…",
    errorTitle: "Не удалось загрузить этот пакет",
    errorDetail: "Содержимое пакета прочитать не удалось. Попробуйте ещё раз.",
    version: "Версия",
    currency: "Рекомендуемая валюта",
    currencyNote: "Валюта, которую предлагает пакет. Установка пакета не меняет вашу валюту.",
    measuredKinds: "Мерные складские единицы",
    none: "Нет",
    tabs: {
      steps: "Операции",
      units: "Единицы",
      qc: "Качество",
      waste: "Отходы",
      terminology: "Термины",
    },
    steps: {
      colRef: "Обозначение",
      colName: "Операция",
      colWorkCenter: "Шаблон рабочего центра",
      colSetup: "Наладка",
      colRun: "Норма времени",
      colScrap: "Ожидаемый брак",
      colCaptures: "Фиксируется в цехе",
      colQc: "Контроль качества",
      setupMinutes: "{{minutes}} мин",
      runRate: "{{seconds}} с на {{unit}}",
      scrapPct: "{{pct}}%",
      emptyTitle: "В этом пакете нет операций маршрута",
      emptyBody: "Маршруты вы соберёте с нуля на шаге 2.",
    },
    units: {
      colCode: "Код",
      colName: "Название",
      colKind: "Что измеряет",
      colPrecision: "Знаков после запятой",
      emptyTitle: "В этом пакете нет единиц измерения",
      emptyBody: "Ваши существующие единицы измерения останутся нетронутыми.",
    },
    qc: {
      colRef: "Обозначение",
      colName: "Чек-лист",
      colControlPoint: "Где проверяется",
      colItems: "Проверок",
      colFailAction: "При несоответствии",
      colGuidedCut: "Управляемый рез",
      itemCount: "Проверок: {{count}}",
      itemCount_one: "Проверка: {{count}}",
      range: "{{min}}–{{max}} {{unit}}",
      yes: "Да",
      no: "Нет",
      emptyTitle: "В этом пакете нет чек-листов качества",
      emptyBody: "Свои можно будет собрать, когда включится модуль качества.",
    },
    waste: {
      colRef: "Обозначение",
      colName: "Категория",
      colDisposition: "Что делать по умолчанию",
      colRecoverable: "Пригодно к возврату",
      colDimensions: "Учитывает размеры",
      yes: "Да",
      no: "Нет",
      emptyTitle: "В этом пакете нет категорий отходов",
      emptyBody: "Свои можно будет добавить, когда включится модуль отходов.",
    },
    terminology: {
      colKey: "Термин",
      colLabel: "Как называет пакет",
      colOtherLocales: "Другие языки",
      emptyTitle: "Этот пакет оставляет стандартные термины",
      emptyBody:
        "Ничего не переименовывается. Переименовать что угодно самостоятельно можно в настройках терминологии.",
      note: "Установка переименует это на всех экранах. Изменить любое слово можно позже в настройках терминологии.",
    },
  },

  controlPoint: {
    receiving: "Приёмка сырья",
    in_process: "В процессе",
    final: "Окончательный контроль",
  },
  failAction: {
    rework: "Доработка",
    scrap: "Брак",
    quarantine: "Карантин",
    cut_and_retest: "Отрезать и перепроверить",
  },
  disposition: {
    reuse: "Повторное использование",
    regrind: "Вторичная переработка",
    sell: "Продажа",
    dispose: "Утилизация",
    pending: "Решить позже",
  },
  unitKind: {
    length: "Длина",
    mass: "Масса",
    area: "Площадь",
    count: "Количество",
    volume: "Объём",
    time: "Время",
    container: "Тара",
  },
  capture: {
    qty_good: "Количество годного",
    qty_scrap: "Количество брака",
    downtime_reason: "Причина простоя",
    measured_length: "Измеренная длина",
    claimed_length: "Заявленная длина",
    batch_ref: "Обозначение серии",
    shift_handoff_note: "Примечание при передаче смены",
    photo: "Фото",
  },
  measuredKind: {
    barrel: "Барабан",
    bobbin: "Бобина",
    roll: "Рулон",
    coil: "Бухта",
    pallet: "Паллета",
    bag: "Мешок",
    none: "Нет",
  },
};
